// SHA1 hash function that works on server and browser
import sha1 from 'sha1';

import { DataTools } from './DataTools';
import { DataServices } from './DataServices';
import { DataDictionary } from './DataDictionary';
import { DataDictionaryTable } from './DataDictionary';
import { DataFormatter } from '../dataformats/DataFormatter';
import { LogicEngine } from '../LogicEngine/LogicEngine';
import { StringOptions } from './StringOptions';
import { DataFormatterOptions } from '../dataformats/DataFormatterType';

/**
 * Wrapper around a single database record with Active Record style functions.
 * @category Data and API Services
 */
export class DataRecord<T = any> {
	// Name of the data dictionary this record belongs to
	tableName: string = '';

	// The database id for this record
	id: string | null = null;

	// The data values for this record
	data: any | null = null;

	// Store the hash value when the record is loaded so we can see if it has changed
	initialHash: string = '';

	// Flag represents sync status
	isSynced: boolean = false;

	// On-demand load the data dictionary if needed for field validation
	dataDictionary: DataDictionary | undefined;
	dataFormatter: DataFormatter | undefined;

	/** Reference to the DataDictionary Table for this record */
	tableDef: DataDictionaryTable | undefined;

	/* Store and Load memory tables */
	static memTables: { [key: string]: any } = {};

	constructor(theTableName: string, theRecord?: any)
	{
		this.tableName = theTableName;
		if (theRecord)
		{
			this.setData(theRecord);
		}
	}

	/**
	 * Internal function that will review all objects in the data object and
	 * create a hash value.  That is stored in "initialHash" and used to
	 * see if the object has changed at all.
	 */
	rememberDataHash(): void
	{
		this.initialHash = this.getDataHash();
	}

	/**
	 * Internal function that gets a hash value from an object.
	 */
	private getDataHash(): string
	{
		// return sha1(DataTools.toJSON(this.data));
		return sha1(JSON.stringify(this.data));
	}

	/** Validate that the document has an ID.  If not create a random
	 * uuid for the id field.
	 *
	 */
	public validateId()
	{
		if (this.data && this.data.id && this.data.id.length > 0)
		{
			return;
		}

		let parta = new Date().getTime().toString(36);
		let partb = (Math.ceil(Math.random() * 899999) + 1000000).toString(36);
		this.data['id'] = parta + partb;
	}

	/** Returns the Data Dictionary Table Defition for this record */
	async getTableDef(): Promise<DataDictionaryTable>
	{
		if (this.tableDef) return this.tableDef;

		const dataService = DataServices.instance();

		if (!this.dataDictionary) this.dataDictionary = await dataService.getDataDictionary();

		if (this.tableName === '')
		{
			console.warn("DataRecord getTableDef missing table name:", this);
			this.tableName = '#Temp';
		}

		this.tableDef = this.dataDictionary.getTable(this.tableName);
		return this.tableDef;
	}

	/**
	 * Temporary fixup function due to the way the server returns the data as all text
	 * @returns true if fixup is done
	 */
	async fixRecord()
	{
		await this.getTableDef();
		if (!this.tableDef) return false;
		if (!this.dataDictionary) return false;

		let df = new DataFormatter();
		for (let field of this.tableDef.rawDefinition)
		{

			if (!field || !field.field_type)
			{
				console.warn("DataRecord.fixRecord Innvalid field or data type in rawDefinition for table=", this.tableName, "field=", field);
				// return this.data[field.field];
			} else
			{
				const value = df.unformatData(field.field_type, this.data[field.field]);
				this.data[field.field] = value;
			}
		}

		if (this.data['created_dt']) this.data['created_dt'] = new Date(this.data['created_dt']);

		if (this.data['reported_dt']) this.data['reported_dt'] = new Date(this.data['reported_dt']);

		if (typeof (this.data['lock_status']) === "string")
		{
			this.data['lock_status'] = false;
		}

		// this.rememberDataHash();
		return true;
	}

	/**
	 * Return the current value of a field as it would be stored in the database.
	 * Lookup items return the code, boolean items return true/false,
	 * Date items return a Date object, etc...
	 * @param fieldName A string value to get value of a field
	 * @returns A field value
	 */
	getValueDatabase(fieldName: string): any
	{
		if (!this.data) return undefined;
		if (this.data[fieldName] === undefined) return undefined;

		/* Try to use the data formatter first and if not return what is stored */
		if (this.tableDef)
		{
			const field = this.tableDef.getField(fieldName);
			if (field)
			{
				if (field.field_type === 'Lookup' && this.dataDictionary && field.options)
				{
					return this.dataDictionary.getLookupAsCode(field.options, this.data[fieldName]);
				}

				if (!this.dataFormatter) this.dataFormatter = new DataFormatter();

				// console.log(`unformatData('${field.field_type}', '${this.data[fieldName]}')`);
				return this.dataFormatter.unformatData(field.field_type, this.data[fieldName]);
			}
		} else
		{
			console.error(
				'DataRecord getValueDatabase failed because tableDef is not loaded.  Call getTableDef first.',
			);
		}

		return this.data[fieldName];
	}

	/**
	 * Get formatted version of data object
	 */
	getDataFormatted(): any
	{
		const formattedData: any = {};
		for (const field of Object.keys(this.data))
		{
			formattedData[field] = this.getValueFormatted(field);
		}

		return formattedData;
	}

	/** Return the text formatted version of the value for a given field.
	 * A boolean value is return as "Yes" or "No", for example.
	 * @param fieldName A string value to get text formatted field value
	 * @returns "Yes" or "No"
	 */
	getValueFormatted(fieldName: string): string
	{
		if (!this.data) return '';
		if (this.data[fieldName] === undefined) return '';

		/* Try to use the data formatter first and if not return what is stored */
		if (this.tableDef)
		{
			const field = this.tableDef.getField(fieldName);
			if (field)
			{
				if (field.field_type === 'Lookup' && this.dataDictionary && field.options)
				{
					return this.dataDictionary.getLookupAsText(field.options, this.data[fieldName]);
				}

				if (!this.dataFormatter) this.dataFormatter = new DataFormatter();
				return this.dataFormatter.formatData(field.field_type || "Text", this.data[fieldName]);
			}
		} else
		{
			console.error(
				'DataRecord getValueFormatted failed because tableDef is not loaded.  Call getTableDef first.',
			);
		}

		return this.data[fieldName];
	}

	/** Convert the Options column into a StringOptions that can be easily accessed */
	getValueOptions(fieldName: string): StringOptions
	{
		if (!this.data) return new StringOptions("");
		if (this.data[fieldName] === undefined) new StringOptions("");

		/* Try to use the data formatter first and if not return what is stored */
		if (this.tableDef)
		{
			const field = this.tableDef.getField(fieldName);
			if (field)
			{
				const fieldOptions = new StringOptions(field.options);
				return fieldOptions;
			}
		}

		return new StringOptions("");
	}

	/**
	 * Get the dispaly unit of the field
	 * @param fieldName A string value to get value unit
	 * @returns the dispaly uinit of the field
	 */
	getValueUnits(fieldName: string): string
	{
		if (!this.data) return '';
		// if (this.data[fieldName] === undefined) return '';

		/* Try to use the data formatter first and if not return what is stored */
		if (this.tableDef)
		{
			const field = this.tableDef.getField(fieldName);
			if (field)
			{
				if (!this.dataFormatter) this.dataFormatter = new DataFormatter();

				let opt: DataFormatterOptions = {};
				const fieldOptions = new StringOptions(field.options);

				if (fieldOptions.checkOption("displayUnit")) opt.displayunit = fieldOptions.checkOption("displayUnit");
				if (fieldOptions.checkOption("displayUnits")) opt.displayunit = fieldOptions.checkOption("displayUnits");
				if (fieldOptions.checkOption("units")) opt.displayunit = fieldOptions.checkOption("units");
				if (fieldOptions.checkOption("unit")) opt.displayunit = fieldOptions.checkOption("unit");

				if (fieldOptions.checkOption("format")) opt.format = fieldOptions.checkOption("format");

				if (fieldOptions.checkOption("from")) opt.convertfrom = fieldOptions.checkOption("from");
				if (fieldOptions.checkOption("to")) opt.convertfrom = fieldOptions.checkOption("to");

				// console.log("Checking field:", field);
				// console.log("FOUND fieldOp=", fieldOptions, "opt=", opt);

				return this.dataFormatter.displayUnits(field.field_type || "Text", opt);
			} else
			{
				if (LogicEngine.debugOutput)
					console.warn("Missing field:", fieldName, "from table", this.tableDef.tableName);
				return '';
			}
		} else
		{
			console.error(
				'DataRecord getValueFormatted failed because tableDef is not loaded.  Call getTableDef first.',
			);
		}

		return '';
	}

	/**
	 * Get the alignment of the field
	 * @param fieldName A string value to get align of the field
	 * @returns the alignment of the field
	 */
	getValueAlign(fieldName: string): string
	{
		if (!this.data) return '';
		if (this.data[fieldName] === undefined) return '';

		/* Try to use the data formatter first and if not return what is stored */
		if (this.tableDef)
		{
			const field = this.tableDef.getField(fieldName);
			if (field)
			{
				if (!this.dataFormatter) this.dataFormatter = new DataFormatter();
				return this.dataFormatter.getAlign(field.field_type || "Text");
			}
		} else
		{
			console.error(
				'DataRecord getValueFormatted failed because tableDef is not loaded.  Call getTableDef first.',
			);
		}

		return '';
	}

	/** Loop through all fields in the data dictionary for this record.
	 * If there are "Calculation" fields they will get updated
	 * @param logic A Logic Engine
	 */
	async updateCalculations(logic: LogicEngine)
	{
		let force = false;

		const tableDef = await this.getTableDef();
		for (let field of tableDef.rawDefinition)
		{
			if (!field.validation_name) continue;
			if (field.validation_name !== 'Calculation') continue;

			// Not all calculations have a formula.  Some are just
			// noted as a calcuation so that the data team knows they are
			// automatically set someplace else which will also have an
			// audit log with each set.
			if (!field.options) continue;
			if (field.options.length < 1) continue;

			try
			{
				let result: any = logic.processTextReplacement(field.options);

				try
				{
					let testNum = parseFloat(result);
					if (testNum != 0) result = testNum;
				} catch { }

				if (this.data[field.field] != result) force = true;

				this.data[field.field] = result;
			} catch (e)
			{
				console.warn('updateCalculations, error in formula for field:', field);
				DataTools.reportException('updateCalculations', e);
			}
		}

		// Automatically save any changes found during a formula calculation
		await this.saveIfNeeded(force);
	}

	/**
	 * Initialize the record with data
	 * should could a value for theRecord.id
	 *
	 * @param theRecord The object that will become the data to track
	 * @returns initialized record
	 */
	public setData(theRecord: any): DataRecord
	{
		if (!this.data) this.data = {};

		Object.assign(this.data, theRecord);

		// Only change the ID if not already set and it is set within the initialize data
		if (theRecord.id && !this.id)
		{
			this.id = theRecord.id;
		}

		this.rememberDataHash();
		return this;
	}

	/** For tables that are temporary, we can save them to memory instead of the server */
	protected saveToMemory(): boolean
	{
		const tempTableName = this.tableName.trim().toLowerCase();

		this.validateId();

		if (!DataRecord.memTables[tempTableName])
		{
			DataRecord.memTables[tempTableName] = {};
		}

		if (!DataRecord.memTables[tempTableName][this.data.id])
		{
			DataRecord.memTables[tempTableName][this.data.id] = {};
		}

		Object.assign(DataRecord.memTables[tempTableName][this.data.id], this.data);

		if (LogicEngine.debugOutput)
			console.log("DataRecord saveToMemory Table=", tempTableName, "id=", this.data.id, "values=", this.data);

		return true;
	}

	/** Load a memory table record by ID */
	public static loadFromMemoryById(tableName: string, id: string): DataRecord | null
	{
		const tempTableName = tableName.trim().toLowerCase();

		if (!DataRecord.memTables[tempTableName])
		{
			if (LogicEngine.debugOutput)
				console.log("DataRecord loadFromMemoryById table=", tempTableName, "id=", id, ". Missing memory table.");
			return null;
		}

		if (!DataRecord.memTables[tempTableName][id])
		{
			if (LogicEngine.debugOutput)
				console.log("DataRecord loadFromMemoryById table=", tempTableName, "id=", id, ". Missing id within table.");
			return null;
		}

		let data = new DataRecord(tableName, DataRecord.memTables[tempTableName][id]);

		if (LogicEngine.debugOutput)
			console.log("DataRecord loadFromMemoryById table=", tempTableName, "id=", id, ". Found:", data);

		return data;
	}

	/** Load memory records from query.  NOTE:  Does not currently look at the query!  It returns all memory records
	for the given table. */
	public static loadFromMemoryByQuery(tableName: string, query: string): DataRecord[]
	{
		const tempTableName = tableName.trim().toLowerCase();

		if (!DataRecord.memTables[tempTableName])
		{
			if (LogicEngine.debugOutput)
				console.log("DataRecord loadFromMemoryById table=", tempTableName, "query=", query, ". Missing memory table.");
			return [];
		}

		let list: DataRecord[] = [];
		for (const item of DataRecord.memTables[tempTableName])
		{
			let data = new DataRecord(tempTableName, item);
			list.push(data);
		}

		if (LogicEngine.debugOutput)
			console.log("DataRecord loadFromMemoryByQuery table=", tempTableName, "query=", query, ". Found:", list);

		return list;
	}

	/**
	 *
	 * @param forceSave A boolean value that represents force to save
	 * @returns A result of save status as a promise of boolean
	 */
	public async saveIfNeeded(forceSave?: boolean): Promise<boolean>
	{
		if (undefined === forceSave) forceSave = false;

		let currentHash = this.getDataHash();
		if (forceSave || this.initialHash !== currentHash)
		{
			//
			// Save to local database here for offline access
			// TODO: Implement cross platform local save?

			if (this.tableName.startsWith("#"))
			{
				/*  Temporary table names, we don't actually save anything to server */
				this.isSynced = true;
				this.initialHash = currentHash;
				return this.saveToMemory();
			}

			// Check to see if the data is corrupt with an invalid data issue from the strange server headers
			if (this.data["0"] === '<' && this.data["1"] === "!")
			{
				console.error("Critical error: Attempt to save DataRecord to table:", this.tableName, "with invalid data:", this.data);
				return false;
			}

			//
			// Next, save to the server if online
			const ds = DataServices.instance();
			const result = await ds.makeApiCall(
				`api/v1/data/${this.tableName}`,
				{ id: this.id, ...this.data },
				'post',
			);

			// Save a copy of this record to the local cache
			ds.writeResultToCache(
				'api',
				`/api/v1/data/${this.tableName}/get/${this.data.id}`,
				null,
				this.data,
			);

			if (result && result.message)
			{
				console.warn('API Result Message:', result.message);
			}

			// TODO: Check for server validation failed
			this.isSynced = !result.isError;

			// Update the hash to the one that was saved to the server
			this.initialHash = currentHash;

			if (result.isError)
			{
				this.initialHash = '';
				this.isSynced = false;
				return false;
			}
		}

		return true;
	}
}
